// ─── BattleViewer2D – Shared Types & Constants ──────────────────────────────

export const CANVAS_W = 800;
export const CANVAS_H = 450;
export const ARENA_INSET = 20;
export const ARENA_RADIUS = 14;
export const CIRCLE_RADIUS = 24;
export const HP_BAR_W = 46;
export const HP_BAR_H = 5;
export const CHARGE_DURATION = 0.3;
export const BASE_EVENT_INTERVAL = 900;
export const IDLE_SPEED = 0.10;

// How many ambient particles live in the arena
export const AMBIENT_PARTICLE_COUNT = 24;

export const TEAM_COLORS: Record<string, string> = {
  teamOne:   '#4a8fd4',
  teamTwo:   '#d44a4a',
  teamThree: '#4ad4a0',
  teamFour:  '#d4a04a',
};

export const LEFT_TEAMS  = ['teamOne',  'teamThree'];
export const RIGHT_TEAMS = ['teamTwo',  'teamFour'];

// ─── Weapon icon map (unicode/emoji per weapon type) ────────────────────────
export const WEAPON_ICONS: Record<string, string> = {
  Axe:    '🪓',
  Sword:  '⚔',
  Mace:   '🔨',
  Staff:  '🪄',
  Dagger: '🗡',
  Chain:  '⛓',
};

export const SHIELD_ICON = '🛡';
// ─── SVG icon filenames (used for canvas-tinted SVG icons) ──────────────────
export const WEAPON_SVG_NAMES: Record<string, string> = {
  Axe:    'axe',
  Sword:  'sword',
  Mace:   'mace',
  Staff:  'staff',
  Dagger: 'dagger',
  Chain:  'chain',
};

export const SHIELD_SVG_NAME = 'shield';
// ─── Types ──────────────────────────────────────────────────────────────────

export type SpriteState =
  | 'idle'
  | 'charging'
  | 'recoiling'
  | 'dodging'
  | 'dead'
  | 'surrendered'
  | 'collapsed';

export interface Sprite {
  key: string;
  name: string;
  team: string;
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  vx: number;
  vy: number;
  state: SpriteState;
  stateProgress: number;
  stateTargetX: number;
  stateTargetY: number;
  stateOriginX: number;
  stateOriginY: number;
  hp: number;
  maxHp: number;
  flashTimer: number;
  flashColor: string;
  opacity: number;
  race: string;
  sex: string;
  avatarNum: number;
  isLegend: boolean;
  hospitalized: boolean;
  // ─ New fields for upgraded viewer ─
  scale: number;
  shakeX: number;
  shakeY: number;
  rotation: number;
  weaponType: string;
  offHandType: string;
  hasShield: boolean;
  comboCount: number;
  comboTimer: number;
  auraColor: string;
  auraTimer: number;
  breathPhase: number;
  weaponBroken: boolean;
  offHandBroken: boolean;
  shieldBroken: boolean;
}

export interface AnimEvent {
  eventType: string;
  actorKey: string;
  targetKey: string;
  damage: number;
  round: number;
  text: string;
  logColor: string;
  logBold: boolean;
  bodyPart?: string;
  slot?: string;
}

export interface LogEntry {
  id: number;
  text: string;
  color: string;
  bold: boolean;
}

export interface LogRound {
  id: number;
  roundNum: number;
  entries: LogEntry[];
}

export interface DamageFloat {
  id: number;
  x: number;
  y: number;
  vy: number;
  opacity: number;
  text: string;
  color: string;
  fontSize?: number;
  subText?: string;
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  type: 'dust' | 'ember' | 'impact' | 'spark' | 'bleed' | 'heal' | 'trail';
  opacity: number;
}

export interface CameraState {
  offsetX: number;
  offsetY: number;
  zoom: number;
  targetZoom: number;
  targetOffsetX: number;
  targetOffsetY: number;
  shakeX: number;
  shakeY: number;
  shakeTimer: number;
}

export interface NarrationState {
  text: string;
  displayedChars: number;
  timer: number;
  visible: boolean;
}

export interface ParticipantSummary {
  combatantId?: number;
  name: string;
  team: string;
  finalHP: number;
  maxHP: number;
  startingHP?: number;
  damageTaken?: number;
  isDead: boolean;
  surrendered: boolean;
  collapsed: boolean;
  race?: string;
  sex?: string;
  avatar?: number;
  isLegend?: boolean;
  isBot?: boolean;
  level?: number;
  type?: string;
  stance?: string;
  strategy?: string;
  weaponType?: string;
  offHandType?: string;
  hasShield?: boolean;
  // Combat stats
  damage?: number;
  highestDamageDealt?: number;
  attacksMade?: number;
  dodgedAttacks?: number;
  parries?: number;
  blockedAttacks?: number;
  missedAttacks?: number;
  criticalHits?: number;
  taunts?: number;
  permanentlyDied?: boolean;
}

export interface BattleData {
  id: number;
  title: string;
  battleType: string;
  reportData: {
    combatLog: string[];
    participantSummaries: Record<string, ParticipantSummary>;
    winner: string;
    roundNumber: number;
  };
}

/** Event types that deal direct HP-reducing damage to a target */
export const DAMAGE_EVENTS = new Set([
  'hit', 'crit', 'momentum_hit', 'graze', 'momentum_graze',
  'block_partial', 'dodge_partial', 'axe_cleave', 'counter_dagger',
  'bleed_tick', 'racial_undead_leech',
]);

/** Attack events that should trigger a micro-charge from actor toward target */
export const ATTACK_EVENTS = new Set([
  'hit', 'crit', 'momentum_hit', 'graze', 'momentum_graze',
  'block_partial', 'dodge_partial', 'axe_cleave', 'counter_dagger',
  'miss', 'momentum_miss',
]);
